import { createContext, useCallback, useContext, useRef, useState, type ReactNode } from 'react';
import type { DateRange } from '@/types/daterange';
import { config } from '@/config';
import { noop } from '@/lib/noop';
import { currentDate } from '@/lib/dates';
import { readStoredDateRange, writeStoredDateRange } from '@/lib/dateRangePresets';

export interface CurrentUser {
  id: string;
  name: string;
  email: string;
  role: 'admin' | 'member';
  avatarUrl?: string;
}

export interface CurrentOrg {
  id: string;
  name: string;
  slug: string;
}

export interface AppContextValue {
  currentUser: CurrentUser;
  currentOrg: CurrentOrg;
  setCurrentUser: (u: CurrentUser) => void;
  setCurrentOrg: (o: CurrentOrg) => void;
  sidebarCollapsed: boolean;
  setSidebarCollapsed: (v: boolean) => void;
  dateRange: DateRange;
  setDateRange: (v: DateRange) => void;
  mockupMode: boolean;
  setMockupMode: (v: boolean) => void;
  signOut: () => void;
  setSignOut: (fn: () => void) => void;
  getToken: () => Promise<string | null>;
  setGetToken: (fn: () => Promise<string | null>) => void;
  /**
   * Flips from `false` to `true` the first time `setGetToken` is called
   * with the real Clerk resolver. Mount-time effects that need to call
   * `getToken()` (e.g. PlatformAdminProvider's gate probe) must wait on
   * this signal — otherwise they fire while `getToken` is still the
   * `noopToken` placeholder and hit unauthenticated 401s. Stays `false`
   * in mockup mode (ClerkSync never runs); consumers that mockup-short-
   * circuit must do so before checking this flag. Task 148.
   */
  tokenResolverReady: boolean;
  /**
   * Ask Clerk to refetch the active organization so downstream components
   * (OrganizationSwitcher, useOrganization consumers) pick up server-side
   * changes to the org (e.g. a rename via Settings › Organization).
   * ClerkSync registers the real implementation; in mockup mode it's a noop.
   */
  reloadOrg: () => Promise<void>;
  setReloadOrg: (fn: () => Promise<void>) => void;
}

const MOCKUP_MODE_KEY = 'kanjoy:mockup-mode';

// eslint-disable-next-line react-refresh/only-export-components -- exported for test-only direct Provider construction (PlatformAdminContext.test.tsx); production code uses AppProvider + useAppContext
export const AppContext = createContext<AppContextValue | null>(null);

// eslint-disable-next-line react-refresh/only-export-components -- hook colocated with provider is a common React context pattern; split would add friction without safety benefit
export function useAppContext(): AppContextValue {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error('useAppContext must be used within AppProvider');
  return ctx;
}

// Fixture values for mockup mode
const MOCKUP_USER: CurrentUser = {
  id: 'usr-sarah',
  name: 'Sarah Chen',
  email: 'schen@firm.com',
  role: 'admin',
};

const MOCKUP_ORG: CurrentOrg = {
  id: 'org-meridian',
  name: 'Meridian & Associates',
  slug: 'meridian',
};

// Empty placeholders for live mode — ClerkSync fills these once auth resolves.
// Keeping id empty ensures data-source hooks (enabled: !!currentOrg.id) stay
// disabled until real identity is available, preventing API calls with fixture IDs.
const EMPTY_USER: CurrentUser = {
  id: '',
  name: '',
  email: '',
  role: 'member',
};

const EMPTY_ORG: CurrentOrg = {
  id: '',
  name: '',
  slug: '',
};

function readStoredMockupMode(): boolean {
  if (!import.meta.env.DEV) return config.mockupMode;
  try {
    const stored = localStorage.getItem(MOCKUP_MODE_KEY);
    if (stored === 'true') return true;
    if (stored === 'false') return false;
  } catch {
    // localStorage unavailable (SSR, privacy mode) — fall through
  }
  return config.mockupMode;
}

export function AppProvider({ children }: { children: ReactNode }) {
  const initialMockup = readStoredMockupMode();
  const [currentUser, setCurrentUser] = useState<CurrentUser>(
    initialMockup ? MOCKUP_USER : EMPTY_USER,
  );
  const [currentOrg, setCurrentOrg] = useState<CurrentOrg>(initialMockup ? MOCKUP_ORG : EMPTY_ORG);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [dateRange, setDateRangeState] = useState<DateRange>(() =>
    readStoredDateRange(currentDate()),
  );
  const setDateRange = useCallback((next: DateRange) => {
    setDateRangeState(next);
    writeStoredDateRange(next);
  }, []);
  const [mockupMode, setMockupModeState] = useState(readStoredMockupMode);
  const signOutRef = useRef<() => void>(noop);
  const signOut = useCallback(() => signOutRef.current(), []);
  const setSignOut = useCallback((fn: () => void) => {
    signOutRef.current = fn;
  }, []);
  const noopToken = useCallback(async () => null as string | null, []);
  const getTokenRef = useRef<() => Promise<string | null>>(noopToken);
  const getToken = useCallback(() => getTokenRef.current(), []);
  const [tokenResolverReady, setTokenResolverReady] = useState(false);
  const setGetToken = useCallback((fn: () => Promise<string | null>) => {
    getTokenRef.current = fn;
    // Order matters: the ref must be assigned before flipping the flag,
    // so any effect that re-runs on `tokenResolverReady` finds the real
    // resolver via getToken() and not the noopToken placeholder.
    setTokenResolverReady(true);
  }, []);
  const noopReload = useCallback(async () => {}, []);
  const reloadOrgRef = useRef<() => Promise<void>>(noopReload);
  const reloadOrg = useCallback(() => reloadOrgRef.current(), []);
  const setReloadOrg = useCallback((fn: () => Promise<void>) => {
    reloadOrgRef.current = fn;
  }, []);

  const setMockupMode = (v: boolean) => {
    setMockupModeState(v);
    if (import.meta.env.DEV) {
      try {
        localStorage.setItem(MOCKUP_MODE_KEY, String(v));
      } catch {
        // localStorage unavailable — ignore
      }
    }
  };

  return (
    <AppContext.Provider
      value={{
        currentUser,
        currentOrg,
        setCurrentUser,
        setCurrentOrg,
        sidebarCollapsed,
        setSidebarCollapsed,
        dateRange,
        setDateRange,
        mockupMode,
        setMockupMode,
        signOut,
        setSignOut,
        getToken,
        setGetToken,
        tokenResolverReady,
        reloadOrg,
        setReloadOrg,
      }}
    >
      {children}
    </AppContext.Provider>
  );
}
